import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import "swiper/css/scrollbar";

import { saveAs } from "file-saver";
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import * as XLSX from "xlsx";
import { PropertyFilters } from "@/components/FilterProperty";
import type {
	Attribute,
	BlockAttributes,
	CondominiumAttributes,
	LeisureData,
	PhotoItem,
	PropertyFormData,
} from "@/components/form-property/property-types";
import { PropertyCardWrapper } from "@/components/PropertyCardWrapper";
import { Sidebar } from "@/components/Sidebar";
import { UpdateNotification } from "@/components/UpdateNotification";
import { booleanToSimNao } from "@/utils/booleanToSimNao";
import { formatCurrency } from "@/utils/formatCurrency";

// Função para formatar área removendo .00
const formatArea = (value: number | string | undefined): string => {
	if (!value || value === "N/A") return "N/A";
	const numValue = typeof value === "string" ? parseFloat(value) : value;
	if (isNaN(numValue)) return "N/A";
	// Remove .00 se for número inteiro
	return numValue % 1 === 0 ? numValue.toFixed(0) : numValue.toString();
};

interface Property {
	id: number;
	attributes: PropertyFormData;
}

interface Stats {
	rental: number;
}
export function Home() {
	const [propertyType, setPropertyType] = useState<string[]>([]);
	const [region, setRegion] = useState<string[]>([]);
	const [status, setStatus] = useState<string[]>([]);
	const [type, setType] = useState<string[]>([]);
	const [searchQuery, setSearchQuery] = useState<string>("");
	const [priceRange, setPriceRange] = useState("");
	const [sidebarOpen, setSidebarOpen] = useState(false);
	const [showLogoutModal, setShowLogoutModal] = useState(false);
	const [lado, setLado] = useState<string[]>([]);
	const [condominium, setCondominium] = useState<string[]>([]);
	const [bedrooms, setBedrooms] = useState<string[]>([]);
	const [usableAreaMin, setUsableAreaMin] = useState("");
	const [usableAreaMax, setUsableAreaMax] = useState("");
	const [captador, setCaptador] = useState<string[]>([]);
	const [praia, setPraia] = useState<string>("");
	const [totalResults, setTotalResults] = useState<number>(0);
	const [stats, setStats] = useState<Stats>({
		rental: 0,
	});

	const [appliedFilters, setAppliedFilters] = useState({
		propertyType: [] as string[],
		region: [] as string[],
		priceRange: "",
		status: [] as string[],
		type: [] as string[],
		searchQuery: "",
		lado: [] as string[],
		condominium: [] as string[],
		usableAreaMin: "",
		usableAreaMax: "",
		bedrooms: [] as string[],
		captador: [] as string[],
		praia: "",
	});

	const [properties, setProperties] = useState<Property[]>([]);
	const [currentPage, setCurrentPage] = useState<number>(1);
	const [totalPages, setTotalPages] = useState<number>(0);
	const [loading, setLoading] = useState<boolean>(false);
	const userCollaborator = localStorage.getItem("userCollaborator");
	const isAdmin = userCollaborator === "ADMIN";
	const itemsPerPage = 10;

	const route = import.meta.env.VITE_URL_ENDPOINT;
	const navigate = useNavigate();

	// Função de logout
	const handleLogout = () => {
		localStorage.removeItem("userCollaborator");
		localStorage.removeItem("userName");
		navigate("/");
	};

	const buildFilters = useCallback(() => {
		const params = new URLSearchParams();

		if (appliedFilters.propertyType.length) {
			appliedFilters.propertyType.forEach((pt) => {
				params.append("filters[propertyType][$in][]", pt);
			});
		}

		if (appliedFilters.status.length) {
			appliedFilters.status.forEach((s) => {
				params.append("filters[status][$in][]", s);
			});
		}

		if (appliedFilters.priceRange) {
			const [minPrice, maxPrice] = appliedFilters.priceRange.split("~");
			if (minPrice) {
				params.append("filters[price][$gte]", String(Number(minPrice)));
			}
			if (maxPrice) {
				params.append("filters[price][$lte]", String(Number(maxPrice)));
			}
		}

		if (appliedFilters.region.length) {
			appliedFilters.region.forEach((r) => {
				params.append("filters[region][$in][]", r);
			});
		}

		if (appliedFilters.type.length) {
			appliedFilters.type.forEach((t) => {
				params.append("filters[type][$in][]", t);
			});
		}

		if (appliedFilters.searchQuery) {
			const searchValue = appliedFilters.searchQuery.trim();

			// Busca em campos de texto (endereço)
			params.append("filters[$or][0][street][$containsi]", searchValue);
			params.append("filters[$or][1][neighborhood][$containsi]", searchValue);
			params.append("filters[$or][2][region][$containsi]", searchValue);

			// Busca em tagKanlo com valor COMPLETO (ex: "CA120")
			params.append("filters[$or][3][tagKanlo][$containsi]", searchValue);

			// Se houver números, extrair e buscar em codigo
			const numericValue = searchValue.replace(/[^0-9]/g, "");
			if (numericValue) {
				// Busca em codigo com número extraído (ex: "120")
				params.append("filters[$or][4][codigo][$eq]", numericValue);
			}
		}

		// Novos filtros
		if (appliedFilters.lado.length) {
			appliedFilters.lado.forEach((l) => {
				params.append("filters[lado][$in][]", l);
			});
		}

		if (appliedFilters.condominium.length) {
			appliedFilters.condominium.forEach((c) => {
				params.append("filters[condominium][Name][$in][]", c);
			});
		}

		if (appliedFilters.usableAreaMin) {
			params.append("filters[usableArea][$gte]", appliedFilters.usableAreaMin);
		}
		if (appliedFilters.usableAreaMax) {
			params.append("filters[usableArea][$lte]", appliedFilters.usableAreaMax);
		}

		if (appliedFilters.bedrooms.length) {
			appliedFilters.bedrooms.forEach((b) => {
				params.append("filters[numberOfBedrooms][$in][]", b);
			});
		}

		if (appliedFilters.captador.length) {
			appliedFilters.captador.forEach((c) => {
				params.append("filters[captadores][username][$in][]", c);
			});
		}

		if (appliedFilters.praia) {
			params.append("filters[praia][$containsi]", appliedFilters.praia);
		}

		return params.toString();
	}, [appliedFilters]);

	useEffect(() => {
		const fetchStats = async () => {
			try {
				// Buscar contadores por status em paralelo
				const [rentalResponse] = await Promise.all([
					fetch(
						`${route}/api/properties?filters[status][$eq]=Ativo&pagination[pageSize]=1`,
					),
				]);

				const [rentalData] = await Promise.all([rentalResponse.json()]);

				const statsData = {
					rental: rentalData.meta?.pagination?.total || 0,
				};
				setStats(statsData);
			} catch (error) {
				console.error("Erro ao buscar estatísticas:", error);
			}
		};

		fetchStats();
	}, [route]);

	useEffect(() => {
		const fetchProperties = async () => {
			try {
				setLoading(true);
				const filters = buildFilters();
				const requestUrl = `${route}/api/properties?pagination[page]=${currentPage}&pagination[pageSize]=${itemsPerPage}&populate=condominium,block,photos,captadores&${filters}`;
				const response = await fetch(requestUrl);
				const data = await response.json();
				let fetchedProps = data.data || [];

				if (!isAdmin) {
					fetchedProps = fetchedProps.filter(
						(p: { attributes: { status: string } }) =>
							p.attributes.status !== "Ativo",
					);
				}

				setProperties(fetchedProps);
				setTotalPages(data.meta.pagination.pageCount || 1);
				setTotalResults(data.meta.pagination.total || fetchedProps.length || 0);
			} catch (error) {
				console.error("Erro ao buscar propriedades:", error);
				setTotalResults(0);
			} finally {
				setLoading(false);
			}
		};
		fetchProperties();
	}, [currentPage, appliedFilters, route, buildFilters, isAdmin]);

	const handleNextPage = () => {
		if (currentPage < totalPages) setCurrentPage(currentPage + 1);
	};

	const handlePrevPage = () => {
		if (currentPage > 1) setCurrentPage(currentPage - 1);
	};

	const handleSearch = () => {
		setAppliedFilters({
			propertyType,
			region,
			priceRange,
			status,
			type,
			searchQuery,
			lado,
			condominium,
			usableAreaMin,
			usableAreaMax,
			bedrooms,
			captador,
			praia,
		});
		setCurrentPage(1);
	};

	const clearFilters = () => {
		setPropertyType([]);
		setRegion([]);
		setPriceRange("");
		setStatus([]);
		setType([]);
		setSearchQuery("");
		setLado([]);
		setCondominium([]);
		setUsableAreaMin("");
		setUsableAreaMax("");
		setBedrooms([]);
		setCaptador([]);
		setPraia("");
		setAppliedFilters({
			propertyType: [],
			region: [],
			priceRange: "",
			status: [],
			type: [],
			searchQuery: "",
			lado: [],
			condominium: [],
			usableAreaMin: "",
			usableAreaMax: "",
			bedrooms: [],
			captador: [],
			praia: "",
		});
		setCurrentPage(1);
	};

	function getCondoAttributes(prop: PropertyFormData): CondominiumAttributes {
		const c = prop.condominium as unknown as {
			data?: { attributes?: CondominiumAttributes };
		};
		return c?.data?.attributes || ({} as CondominiumAttributes);
	}

	function getBlockAttributes(prop: PropertyFormData): BlockAttributes {
		const b = prop.block as unknown as {
			data?: { attributes?: BlockAttributes };
		};
		return b?.data?.attributes || ({} as BlockAttributes);
	}

	function getLeisureAttributes(prop: PropertyFormData): LeisureData {
		const l = prop.leisure as unknown as {
			data?: { attributes?: LeisureData };
		};
		if (l?.data?.attributes) {
			return l.data.attributes;
		}
		return {
			id: 0,
			partyHall: false,
			adultPool: false,
			gameRoom: false,
			kidsPool: false,
			gym: false,
			playground: false,
			sportsCourt: false,
			sauna: false,
			tennisCourt: false,
			bbqArea: false,
			squashCourt: false,
			socialHall: false,
			wetDeckPool: false,
			lapPool: false,
			kidsBeachPool: false,
			solarium: false,
			poolBar: false,
			massageRoom: false,
			womenSpace: false,
			petPlace: false,
			youthGameRoom: false,
			toyLibrary: false,
			pilatesSpace: false,
			lounge: false,
			gourmetSpace: false,
			kidsPartyRoom: false,
			adultPartyRoomWithBar: false,
			adultGameRoom: false,
			readingRoom: false,
			hammockArea: false,
			zenSpace: false,
			coworkingSpace: false,
			adultTVRoom: false,
			gourmetBar: false,
			cinemaRoom: false,
			sacada: false,
			property: { data: null },
		};
	}

	const handleExport = async () => {
		try {
			setLoading(true);
			const filters = buildFilters();
			const requestUrl = `${route}/api/properties?populate=condominium,block,leisure,photos&${filters}`;
			const response = await fetch(requestUrl);
			const data = await response.json();
			const props: Property[] = data.data || [];
			const rows: Record<string, string>[] = [];

			for (const p of props) {
				const prop = p.attributes;
				const condo = getCondoAttributes(prop);
				const block = getBlockAttributes(prop);
				const leisure = getLeisureAttributes(prop);
				const row = {
					ID: String(p.id),
					TipoPropriedade: prop.propertyType || "",
					Tipo: prop.type || "",
					Status: prop.status || "",
					Rua: prop.street || "",
					Número: prop.number || "",
					Bairro: prop.neighborhood || "",
					Região: prop.region || "",
					CEP: prop.zipCode || "",
					ÁreaTotal: prop.totalArea || "",
					ÁreaÚtil: prop.usableArea || "",
					ÁreaConstruida: prop.areaConstruida || "",
					ÁreaTerreno: prop.areaTerreno || "",
					ÁreaComum: prop.commonArea || "",
					ÁreaPrivativa: prop.privateArea || "",
					DistanciaPraia: prop.distanceFromBeach || "",
					Preço: prop.price || "",
					Condomínio: prop.condominiumFee || "",
					IPTU: prop.iptu || "",
					AceitaPermuta: booleanToSimNao(prop.barter),
					Financiamento: booleanToSimNao(prop.financing),
					MétodoPagamento: prop.paymentMethod || "",
					VistaMar: booleanToSimNao(prop.seaView),
					ObservacaoInterna: prop.internalObservation || "",
					ObservacaoExterna: prop.externalObservation || "",
					CondNome: condo.Name || "",
					CondRegião: condo.region || "",
					CondRua: condo.street || "",
					CondBairro: condo.neighborhood || "",
					CondNúmero: condo.number || "",
					CondCEP: condo.zipCode || "",
					CondStatusComercial: condo.commercialStatus || "",
					CondConstrutora: condo.builderName || "",
					CondIncorporadora: condo.developerName || "",
					CondSíndico: condo.managerName || "",
					CondTelSíndico: condo.managerPhone || "",
					CondZelador: condo.caretakerName || "",
					CondTelZelador: condo.caretakerPhone || "",
					CondAnoConstrucao: condo.constructionYear || "",
					CondNroElevServ: condo.numberOfServiceElevators || "",
					CondNroElevSocial: condo.numberOfPassengerElevators || "",
					CondPrazoEntrega: condo.deliverySchedule || "",
					CondEsquina: booleanToSimNao(condo.corner),
					CondGuarita: booleanToSimNao(condo.guardhouse),
					CondInterfone: booleanToSimNao(condo.intercom),
					CondTerraco: booleanToSimNao(condo.terrace),
					CondGaragemSubsolo: booleanToSimNao(condo.undergroundGarage),
					CondServicoPraia: booleanToSimNao(condo.beachService),
					CondGuardaEquipPraia: booleanToSimNao(condo.beachEquipmentStorage),
					CondPortaoEletronico: booleanToSimNao(condo.electronicGate),
					CondGaragemTerrea: booleanToSimNao(condo.groundFloorGarage),
					CondGasColetivo: booleanToSimNao(condo.collectiveGas),
					BlocoNome: block.name || "",
					BlocoNroAndares: block.numberOfFloors || "",
					BlocoAptosPorAndar: block.apartmentsPerFloor || "",
					BlocoTérreoOuAndar: block.groundOrFloor || "",
					BlocoAndarNumero: block.FloorNumber || "",
					BlocoFaseConstrucao: block.ConstructionPhase || "",
					LazerSalaoFestas: booleanToSimNao(leisure.partyHall),
					LazerPiscinaAdulto: booleanToSimNao(leisure.adultPool),
					LazerSalaJogos: booleanToSimNao(leisure.gameRoom),
					LazerPiscinaInfantil: booleanToSimNao(leisure.kidsPool),
					LazerAcademia: booleanToSimNao(leisure.gym),
					LazerPlayground: booleanToSimNao(leisure.playground),
					LazerQuadraEsportiva: booleanToSimNao(leisure.sportsCourt),
					LazerSauna: booleanToSimNao(leisure.sauna),
					LazerQuadraTenis: booleanToSimNao(leisure.tennisCourt),
					LazerChurrasqueira: booleanToSimNao(leisure.bbqArea),
					LazerQuadraSquash: booleanToSimNao(leisure.squashCourt),
					LazerSalaoSocial: booleanToSimNao(leisure.socialHall),
					LazerPiscinaDeckMolhado: booleanToSimNao(leisure.wetDeckPool),
					LazerPiscinaRaia: booleanToSimNao(leisure.lapPool),
					LazerPiscinaInfPraia: booleanToSimNao(leisure.kidsBeachPool),
					LazerSolarium: booleanToSimNao(leisure.solarium),
					LazerBarPiscina: booleanToSimNao(leisure.poolBar),
					LazerSalaMassagem: booleanToSimNao(leisure.massageRoom),
					LazerEspacoMulher: booleanToSimNao(leisure.womenSpace),
					LazerPetPlace: booleanToSimNao(leisure.petPlace),
					LazerSalaJogosJuvenil: booleanToSimNao(leisure.youthGameRoom),
					LazerBrinquedoteca: booleanToSimNao(leisure.toyLibrary),
					LazerEspacoPilates: booleanToSimNao(leisure.pilatesSpace),
					LazerLounge: booleanToSimNao(leisure.lounge),
					LazerEspacoGourmet: booleanToSimNao(leisure.gourmetSpace),
					LazerSalaoFestasInfantil: booleanToSimNao(leisure.kidsPartyRoom),
					LazerSalaoFestasAdultoBar: booleanToSimNao(
						leisure.adultPartyRoomWithBar,
					),
					LazerSalaJogosAdulto: booleanToSimNao(leisure.adultGameRoom),
					LazerSalaLeitura: booleanToSimNao(leisure.readingRoom),
					LazerHammockArea: booleanToSimNao(leisure.hammockArea),
					LazerZenSpace: booleanToSimNao(leisure.zenSpace),
					LazerCoworking: booleanToSimNao(leisure.coworkingSpace),
					LazerSalaTVAdulto: booleanToSimNao(leisure.adultTVRoom),
					LazerGourmetBar: booleanToSimNao(leisure.gourmetBar),
					LazerCinema: booleanToSimNao(leisure.cinemaRoom),
					LazerSacada: booleanToSimNao(leisure.sacada),
				};
				rows.push(row);
			}

			const worksheet = XLSX.utils.json_to_sheet(rows);
			const workbook = XLSX.utils.book_new();
			XLSX.utils.book_append_sheet(workbook, worksheet, "Propriedades");
			const wbout = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
			const blob = new Blob([wbout], { type: "application/octet-stream" });
			saveAs(blob, "imoveis.xlsx");
		} catch (error) {
			console.error("Erro ao exportar propriedades:", error);
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		const user = localStorage.getItem("userCollaborator");
		if (!user) {
			navigate("/");
		}
	}, [navigate]);

	const statusOptions = [
		{ value: "Ativo", label: "Ativo" },
		{ value: "Vendido", label: "Vendido" },
		...(isAdmin ? [{ value: "Disponível", label: "Disponível" }] : []),
	];

	const propertyTypeOptions = [
		{ value: "apartment", label: "Apartamento" },
		{ value: "house", label: "Casa" },
		{ value: "area", label: "Área" },
		{ value: "terrain", label: "Terreno" },
	];

	const priceRangeOptions = [
		{ value: "~400000", label: "Até R$ 400.000" },
		{ value: "400000~600000", label: "R$ 400.000 - R$ 600.000" },
		{ value: "600000~800000", label: "R$ 600.000 - R$ 800.000" },
		{ value: "800000~1000000", label: "R$ 800.000 - R$ 1.000.000" },
		{ value: "1000000~2000000", label: "R$ 1.000.000 - R$ 2.000.000" },
		{ value: "2000000~", label: "Acima de R$ 2.000.000" },
	];

	let subtypeOptions: { value: string; label: string }[] = [];
	if (propertyType.length) {
		propertyType.forEach((pt) => {
			switch (pt) {
				case "apartment":
					subtypeOptions = [
						...subtypeOptions,
						{ value: "Apartamento", label: "Apartamento" },
						{ value: "Flat", label: "Flat" },
						{ value: "Cobertura", label: "Cobertura" },
						{ value: "Gardem", label: "Gardem" },
						{ value: "Kitnet/Studio", label: "Kitnet / Studio" },
					];
					break;
				case "house":
					subtypeOptions = [
						...subtypeOptions,
						{ value: "Casa", label: "Casa" },
						{ value: "Sobrado", label: "Sobrado" },
						{ value: "Duplex", label: "Duplex" },
					];
					break;
				case "commercial_property":
					subtypeOptions = [
						...subtypeOptions,
						{ value: "Pousada", label: "Pousada" },
						{ value: "Conjunto", label: "Conjunto" },
						{ value: "Loja", label: "Loja" },
						{ value: "Prédio", label: "Prédio" },
						{ value: "Sala", label: "Sala" },
						{ value: "Galpão", label: "Galpão" },
						{ value: "Hotel", label: "Hotel" },
					];
					break;
				case "area":
					subtypeOptions = [
						...subtypeOptions,
						{ value: "Residencial", label: "Residencial" },
						{ value: "Comercial", label: "Comercial" },
						{ value: "Industrial", label: "Industrial" },
					];
					break;
				default:
					break;
			}
		});
		subtypeOptions = subtypeOptions.filter(
			(option, index, self) =>
				index === self.findIndex((o) => o.value === option.value),
		);
	}

	return (
		<div className="flex min-h-screen bg-gray-50">
			<Sidebar
				sidebarOpen={sidebarOpen}
				setSidebarOpen={setSidebarOpen}
				isAdmin={isAdmin}
			/>

			{/* Main Content */}
			<div className="flex flex-1 flex-col lg:ml-0">
				{/* Top Header */}
				<header className="border-b border-gray-200 bg-white shadow-sm">
					<div className="flex h-16 items-center justify-between px-4 sm:px-6 lg:px-8">
						<div className="flex items-center">
							<button
								onClick={() => setSidebarOpen(!sidebarOpen)}
								className="rounded-md p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-500 lg:hidden"
							>
								<i className="fas fa-bars text-xl"></i>
							</button>
							<h1 className="ml-4 text-xl font-semibold text-gray-900 sm:text-2xl lg:ml-0">
								Sistema Iguatemar Imóveis
							</h1>
						</div>
						<div className="flex items-center space-x-4">
							<button
								onClick={handleExport}
								className="flex items-center space-x-2 rounded-lg bg-green-600 px-3 py-2 text-sm font-medium text-white hover:bg-green-700 sm:px-4"
							>
								<i className="fas fa-download"></i>
								<span className="hidden sm:inline">Exportar</span>
							</button>
						</div>
					</div>
				</header>

				<UpdateNotification />

				{/* Stats Cards */}
				<div className="bg-white p-4 shadow-sm sm:p-6">
					<div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
						<div className="rounded-lg bg-gradient-to-r from-emerald-600 to-emerald-700 p-4 text-white sm:p-6">
							<div className="flex items-center">
								<div className="flex-shrink-0">
									<i className="fas fa-check-circle text-xl sm:text-2xl"></i>
								</div>
								<div className="ml-3 sm:ml-4">
									<p className="text-xs font-medium text-emerald-100 sm:text-sm">
										Imóveis à venda (Ativo)
									</p>
									<p className="text-2xl font-bold sm:text-3xl">
										{stats.rental}
									</p>
								</div>
							</div>
						</div>
					</div>
				</div>

				<PropertyFilters
					// Estados dos filtros existentes
					region={region}
					setRegion={setRegion}
					status={status}
					setStatus={setStatus}
					propertyType={propertyType}
					setPropertyType={setPropertyType}
					type={type}
					setType={setType}
					priceRange={priceRange}
					setPriceRange={setPriceRange}
					searchQuery={searchQuery}
					setSearchQuery={setSearchQuery}
					// Estados dos novos filtros
					lado={lado}
					setLado={setLado}
					condominium={condominium}
					setCondominium={setCondominium}
					usableAreaMin={usableAreaMin}
					setUsableAreaMin={setUsableAreaMin}
					usableAreaMax={usableAreaMax}
					setUsableAreaMax={setUsableAreaMax}
					bedrooms={bedrooms}
					setBedrooms={setBedrooms}
					captador={captador}
					setCaptador={setCaptador}
					praia={praia}
					setPraia={setPraia}
					// Opções
					statusOptions={statusOptions}
					propertyTypeOptions={propertyTypeOptions}
					subtypeOptions={subtypeOptions}
					priceRangeOptions={priceRangeOptions}
					// Funções
					handleSearch={handleSearch}
					clearFilters={clearFilters}
					isAdmin={isAdmin}
				/>

				{/* Content */}

				<main className="flex-1 p-4 sm:p-6">
					<div className="mb-4 flex items-center justify-between">
						<p className="text-base font-semibold text-gray-800">
							{totalResults} imóveis encontrados
						</p>
					</div>

					{loading && (
						<div className="flex items-center justify-center py-16">
							<div className="text-center">
								<div className="mx-auto mb-4 h-12 w-12 animate-spin rounded-full border-4 border-blue-200 border-t-blue-600"></div>
								<p className="text-gray-600">Carregando imóveis...</p>
							</div>
						</div>
					)}

					{!loading && (
						<>
							<div className="space-y-4 sm:space-y-6">
								{properties.length > 0 ? (
									properties.map((property) => {
										if (!property || !property.attributes) return null;
										const attributes = property.attributes;
										const photosData =
											(attributes.photos as { data: PhotoItem[] } | undefined)
												?.data ?? [];

										// Criar mapa de ID para URL
										const photoMap = new Map<number, string>();
										photosData.forEach((photoItem: PhotoItem) => {
											photoMap.set(
												photoItem.id,
												`${route}${photoItem.attributes.url}`,
											);
										});

										// Aplicar photoOrder se existir e contiver IDs válidos
										let images: string[] = [];
										if (
											attributes.photoOrder &&
											Array.isArray(attributes.photoOrder) &&
											attributes.photoOrder.length > 0
										) {
											// Usar photoOrder para ordenar por IDs
											attributes.photoOrder.forEach((photoId: number) => {
												const url = photoMap.get(photoId);
												if (url) {
													images.push(url);
												}
											});
											// Adicionar fotos que não estão no photoOrder
											photoMap.forEach((url, id) => {
												if (!attributes.photoOrder!.includes(id)) {
													images.push(url);
												}
											});
										} else {
											// Ordem padrão
											images = Array.from(photoMap.values());
										}

										const capitalizeFirstLetter = (str: string) => {
											return str.charAt(0).toUpperCase() + str.slice(1);
										};

										let description = "";
										let propertyAttributes: Attribute[] = [];

										if (attributes.propertyType === "apartment") {
											description = "Apartamento";

											propertyAttributes = [
												{
													label: "Quartos",
													value: attributes.numberOfBedrooms || "N/A",
												},
												{
													label: "Suítes",
													value: attributes.numberOfSuites || "N/A",
												},
												{ label: "Vagas", value: attributes.garage || "N/A" },
												{
													label: "Área Útil (m²)",
													value: formatArea(attributes.usableArea),
												},
											];
										} else if (attributes.propertyType === "house") {
											description = "Casa";
											propertyAttributes = [
												{
													label: "Quartos",
													value: attributes.numberOfBedrooms || "N/A",
												},
												{
													label: "Suítes",
													value: attributes.numberOfSuites || "N/A",
												},
												{ label: "Vagas", value: attributes.garage || "N/A" },
												{
													label: "Área Construída (m²)",
													value: formatArea(attributes.areaConstruida),
												},
											];
										} else if (attributes.propertyType === "area") {
											description = "Área";
											propertyAttributes = [
												{
													label: "Área Total (m²)",
													value: formatArea(attributes.totalArea),
												},
											];
										} else if (
											attributes.propertyType === "commercial_property"
										) {
											description = "Comercial";
											propertyAttributes = [
												{
													label: "Banheiros",
													value: attributes.bathrooms || "N/A",
												},
												{
													label: "Guarita",
													value: attributes.guardhouse || "N/A",
												},
												{
													label: "Salas",
													value: attributes.numberOfRooms || "N/A",
												},
												{
													label: "Área Construída (m²)",
													value: formatArea(attributes.totalArea),
												},
											];
										} else if (attributes.propertyType === "terrain") {
											description = "Terreno";
											propertyAttributes = [
												{
													label: "Área Total (m²)",
													value: formatArea(attributes.totalArea),
												},
											];
										} else {
											return null;
										}

										let displayType = "";

										if (
											attributes.type &&
											attributes.type.toLowerCase() !== "apartamento"
										) {
											displayType = capitalizeFirstLetter(attributes.type);
										} else if (
											attributes.propertyType === "apartment" &&
											attributes.type &&
											attributes.type.toLowerCase() === "apartamento"
										) {
											displayType = "";
										} else if (attributes.propertyType !== "apartment") {
											displayType = description;
										}

										const title = displayType
											? `${attributes.neighborhood || ""} - ${displayType}`
											: `${attributes.neighborhood || ""} - ${description}`;
										const additionalInfo = `Região: ${attributes.region || ""}`;

										const condoName = getCondoAttributes(attributes).Name;
										const blockName = getBlockAttributes(attributes).name;

										const apNumber = attributes.apartmentNumber
											? ` - ${attributes.apartmentNumber}`
											: "";

										let locationDescription = `${attributes.street || ""}, ${attributes.number || ""}${apNumber}`;

										if (condoName) {
											locationDescription += ` - ${condoName}`;
										}

										if (blockName && blockName !== condoName) {
											locationDescription += ` - ${blockName}`;
										}

										if (
											attributes.propertyType === "terrain" ||
											attributes.propertyType === "area"
										) {
											const quadra = attributes.quadra
												? `${attributes.street || ""}, ${attributes.number || ""} - Quadra ${attributes.quadra}`
												: "";
											const lote = attributes.lote
												? `Lote(s) ${attributes.lote}`
												: "";
											locationDescription = [quadra, lote]
												.filter(Boolean)
												.join(" - ");
										}

										const cardProps = {
											id: property.id,
											codigo: attributes.codigo, // Código auto-incrementado
											title: title,
											description: locationDescription,
											additionalInfo: additionalInfo,
											status: attributes.status,
											images: images,
											price: formatCurrency(attributes.price),
											type: attributes.type, // Para gerar o ID
											tagKanlo: attributes.tagKanlo || "N/A", // tagKanlo original
											condominiumPrice: attributes.condominiumFee
												? formatCurrency(attributes.condominiumFee)
												: "N/A",
											iptuPrice: attributes.iptu
												? formatCurrency(attributes.iptu)
												: "N/A",
											attributes: propertyAttributes,
											updatedAt: attributes.updatedAt
												? new Date(attributes.updatedAt).toISOString()
												: undefined,
											captador: attributes.captadores,
										};

										return (
											<PropertyCardWrapper
												key={property.id}
												property={property}
												cardProps={cardProps}
											/>
										);
									})
								) : (
									<div className="flex flex-col items-center justify-center py-16 text-center">
										<div className="mb-4 rounded-full bg-gray-100 p-6">
											<i className="fas fa-home text-4xl text-gray-400"></i>
										</div>
										<h3 className="mb-2 text-xl font-semibold text-gray-900">
											Nenhum imóvel encontrado
										</h3>
										<p className="text-gray-600">
											Tente ajustar os filtros para encontrar mais resultados.
										</p>
									</div>
								)}
							</div>

							{!loading && properties.length > 0 && (
								<div className="mt-8 flex flex-col items-center space-y-4 sm:flex-row sm:justify-center sm:space-x-2 sm:space-y-0">
									<button
										onClick={handlePrevPage}
										disabled={currentPage === 1}
										className="flex w-full items-center justify-center space-x-2 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 sm:w-auto"
									>
										<i className="fas fa-chevron-left"></i>
										<span>Anterior</span>
									</button>

									<div className="flex space-x-1">
										{(() => {
											const pages = [];
											const maxVisiblePages = 5;
											const halfVisible = Math.floor(maxVisiblePages / 2);

											let startPage = Math.max(1, currentPage - halfVisible);
											let endPage = Math.min(
												totalPages,
												startPage + maxVisiblePages - 1,
											);

											// Ajustar startPage se endPage estiver no limite
											if (endPage - startPage + 1 < maxVisiblePages) {
												startPage = Math.max(1, endPage - maxVisiblePages + 1);
											}

											// Adicionar primeira página e reticências se necessário
											if (startPage > 1) {
												pages.push(
													<button
														key={1}
														onClick={() => setCurrentPage(1)}
														className="h-10 w-10 rounded-lg border border-gray-300 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50"
													>
														1
													</button>,
												);

												if (startPage > 2) {
													pages.push(
														<span
															key="dots1"
															className="flex h-10 w-10 items-center justify-center text-gray-500"
														>
															...
														</span>,
													);
												}
											}

											// Adicionar páginas visíveis
											for (let i = startPage; i <= endPage; i++) {
												pages.push(
													<button
														key={i}
														onClick={() => setCurrentPage(i)}
														className={`h-10 w-10 rounded-lg text-sm font-medium ${
															currentPage === i
																? "bg-blue-600 text-white"
																: "border border-gray-300 bg-white text-gray-700 hover:bg-gray-50"
														}`}
													>
														{i}
													</button>,
												);
											}

											// Adicionar reticências e última página se necessário
											if (endPage < totalPages) {
												if (endPage < totalPages - 1) {
													pages.push(
														<span
															key="dots2"
															className="flex h-10 w-10 items-center justify-center text-gray-500"
														>
															...
														</span>,
													);
												}

												pages.push(
													<button
														key={totalPages}
														onClick={() => setCurrentPage(totalPages)}
														className="h-10 w-10 rounded-lg border border-gray-300 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50"
													>
														{totalPages}
													</button>,
												);
											}

											return pages;
										})()}
									</div>

									<button
										onClick={handleNextPage}
										disabled={currentPage === totalPages}
										className="flex w-full items-center justify-center space-x-2 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 sm:w-auto"
									>
										<span>Próxima</span>
										<i className="fas fa-chevron-right"></i>
									</button>
								</div>
							)}
						</>
					)}
				</main>
			</div>

			{/* Modal de Logout */}
			{showLogoutModal && (
				<div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
					<div className="w-full max-w-md rounded-lg bg-white p-6 shadow-xl">
						<div className="mb-4 text-center">
							<i className="fas fa-sign-out-alt mb-3 text-4xl text-red-500"></i>
							<h3 className="text-lg font-semibold text-gray-900">
								Confirmar Saída
							</h3>
							<p className="mt-2 text-sm text-gray-600">
								Tem certeza que deseja sair do sistema?
							</p>
						</div>
						<div className="flex space-x-3">
							<button
								onClick={() => setShowLogoutModal(false)}
								className="flex-1 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
							>
								Cancelar
							</button>
							<button
								onClick={handleLogout}
								className="flex-1 rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700"
							>
								Sair
							</button>
						</div>
					</div>
				</div>
			)}

			{/* Mobile sidebar overlay */}
			{sidebarOpen && (
				<div
					className="fixed inset-0 z-40 bg-gray-600 bg-opacity-75 lg:hidden"
					onClick={() => setSidebarOpen(false)}
				></div>
			)}
		</div>
	);
}
